import { fallbackLevelIdForFoundation } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type { FoundationAssemblyEntity } from "building-types/foundations/foundationAssemblyEntityTypes";
import { NONE_VALUE, type FoundationSubSystemType } from "building-types/foundations/foundationEnums";
import type { Foundation, FoundationEdgeOverridesTable } from "building-types/foundations/foundationSystemTypes";
import type { GeneratedFoundationHorizontalAssemblySubSystem } from "building-types/foundations/foundationTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { FoundationSystemSettings } from "building-types/settings/foundationSettingsTypes";
import type { RWTable, Table } from "common/data/table";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { foundationPerimeterInfoToUse } from "../elements/foundation/foundationMirrorUtils";
import { makeFoundationSubSystem } from "../elements/foundation/foundationSubSystem";
import { entityHandleForSelectingReportingUnitFromEntityId } from "../generationTools/getInstanceIdFromLevel";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  levels,
  planarGraphs,
  foundationHorizontalAssembliesPaths,
  datumAbstractions,
  optionChoices,
  entityDisabledByGroup,
  mirror,
  mirrorTranslationX,
  horizontalAssemblyFoundations: foundations,
  horizontalAssemblyFoundationSystems: foundationSystems,
  doc: {
    foundationAssemblies,
    foundationComponents,
    framingComponents,
    slabComponents,
    sheathingComponents,
    optionSets,
    instanceOptionSets,
  },
  idToParents,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula({ foundations, foundationSystems }, definitions, optionChoices);
  return {
    levels,
    planarGraphs,
    foundationHorizontalAssembliesPaths,
    framingComponents,
    slabComponents,
    sheathingComponents,
    foundationSettings: settings.foundation,
    foundationAssemblies,
    foundationComponents,
    foundations,
    foundationSystems,
    datumAbstractions,
    doorSettings: settings.door,
    optionChoices: resolvedOptionChoices,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
    idToParents,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  foundationSettings: "shallow",
  optionChoices: "shallow",
  idToParents: "shallow",
  foundations: "shallow",
  foundationSystems: "shallow",
});

type FoundationPerimeterInfoOptions = NonNullable<Parameters<typeof foundationPerimeterInfoToUse>[5]>;

function foundationSubSystemTypeForHorizontalAssemblyFoundation({
  foundation,
  foundationAssemblies,
  foundationSettingsFromConfig,
}: {
  foundation: Foundation;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationSettingsFromConfig: FoundationSystemSettings;
}): FoundationSubSystemType {
  const assemblyId = foundation.foundationAssemblyId ?? foundationSettingsFromConfig.foundationAssembly;
  const assembly = assemblyId && assemblyId !== NONE_VALUE ? foundationAssemblies[assemblyId] : undefined;
  return assembly?.assemblySystem ?? foundationSettingsFromConfig.foundationSubSystemType;
}

function generate(input: ReturnType<typeof select>): {
  foundationHorizontalAssemblies: Table<GeneratedFoundationHorizontalAssemblySubSystem>;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];

  if (Object.values(input.levels).length === 0) {
    return { foundationHorizontalAssemblies: {}, issues };
  }

  const foundationHorizontalAssemblies = generateFoundationHorizontalAssembliesFromFoundations({
    ...input,
    issues,
  });

  return { foundationHorizontalAssemblies, issues };
}

/**
 * Generates foundation systems from foundation horizontal assemblies.
 * This implementation processes Foundation entities and generates foundations keyed by FoundationId.
 */
function generateFoundationHorizontalAssembliesFromFoundations(
  input: ReturnType<typeof select> & { issues: BuildingGenerationIssue[] },
): RWTable<GeneratedFoundationHorizontalAssemblySubSystem> {
  const {
    levels,
    foundationComponents,
    foundationHorizontalAssembliesPaths,
    foundationSystems,
    foundations,
    datumAbstractions,
    doorSettings,
    issues,
    foundationAssemblies,
    foundationSettings,
    optionChoices,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
    planarGraphs,
    framingComponents,
    slabComponents,
    sheathingComponents,
    idToParents,
  } = input;

  const foundationSettingsFromConfig = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSettingsFromConfig,
  );

  const foundationSystem: RWTable<GeneratedFoundationHorizontalAssemblySubSystem> = {};

  const resolvedFoundationSystems = evalFormulas(
    foundationSystems,
    (v) => optionChoices[v],
    undefined,
    entityDisabledByGroup,
  );
  const resolvedFoundations = evalFormulas(foundations, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const foundationId in resolvedFoundations) {
    const foundation = resolvedFoundations[foundationId];
    if (!foundation || foundation.disabled) {
      continue;
    }
    const foundationSystemId = foundation.parentId;
    if (resolvedFoundationSystems[foundationSystemId]?.disabled) {
      continue;
    }
    const firstLevelId = fallbackLevelIdForFoundation(foundation, idToParents, levels);
    if (!firstLevelId || !levels[firstLevelId]) {
      throw new Error(`First level not found`);
    }
    const firstLevel = levels[firstLevelId];

    /**
     * @note there is some redundancy here because foundationGraph should be availble via paths->planarGraphId in the makeFoundationSubSystem function.
     * WE ARE PASSING IN THE POLYGON PLANAR GRAPH TABLE IN THAT FUNCTION.
     */
    const foundationGraph = planarGraphs[foundationId];
    if (!foundationGraph) {
      console.error(`Foundation graph not found for foundation ${foundationId}`);
      continue;
    }

    const foundationPaths = foundationHorizontalAssembliesPaths[foundationId];
    if (!foundationPaths) {
      console.error(`Foundation paths not found for foundation ${foundationId}`);
      continue;
    }

    // Validate that all paths reference the correct planar graph
    const pathEntries = Object.entries(foundationPaths);
    if (pathEntries.length === 0) {
      console.error(`Foundation paths are empty for foundation ${foundationId}`);
      continue;
    }

    // Validate that the planar graph structure is complete
    const { planarGraph } = foundationGraph;
    if (!planarGraph.vertexHalfEdges || planarGraph.vertexHalfEdges.length === 0) {
      console.error(`Foundation graph has incomplete vertexHalfEdges structure for foundation ${foundationId}`);
      continue;
    }

    /**
     * @note We need to discuss the settings part of this implementation.
     * The previous step defaults to the foundationAssembly from the foundationSettings in the absence of a foundationAssemblyOverride->foundationAssembly.
     * It is also defaulting to the system constraints in the absence of foundationAssemblyOverride->datums.
     *       ? Should we use the defaults from the foundation settings also. if true then we need to know the type of
     *         the foundation super system here as well because the way our settings are organized, they are a function of
     *         the foundation super system type and foundation sub system type.
     */
    const levelToUse =
      "levelId" in foundation && foundation.levelId && levels[foundation.levelId]
        ? levels[foundation.levelId]
        : firstLevel;
    const levelIdToUse = levelToUse.id;

    const foundationSubSystemType = foundationSubSystemTypeForHorizontalAssemblyFoundation({
      foundation,
      foundationAssemblies,
      foundationSettingsFromConfig,
    });

    const foundationSubSystem = makeFoundationSubSystem(foundationPaths, {
      level: levelToUse,
      planarGraph: foundationGraph,
      planarGraphs,
      doorSettings,
      datumAbstractions,
      slabSettings: foundationSettingsFromConfig,
      foundationSubSystemType,
      foundationComponents: finalizedFoundationComponents,
      framingComponents,
      slabComponents,
      sheathingComponents,
      issues,
    });

    if (!foundation.perimeter || !foundation.edgeIds) {
      console.error(`Foundation perimeter or edgeIds not found for foundation ${foundationId}`);
      continue;
    }

    const foundationPerimeterInfoOptions: FoundationPerimeterInfoOptions = {
      datumAbstractions,
      foundationLateralDatumId: foundation.lateralDatumId,
    };

    const { perimeter } = foundationPerimeterInfoToUse(
      foundation.perimeter,
      foundation.edgeIds,
      (foundation as { foundationEdgeOverrides?: FoundationEdgeOverridesTable }).foundationEdgeOverrides,
      mirror,
      mirrorTranslationX,
      foundationPerimeterInfoOptions,
    );

    foundationSystem[foundationId] = {
      ...buildingEntityBaseNew(
        "FOUNDATION_SYSTEM_HORIZONTAL_ASSEMBLY",
        foundationId,
        entityHandleForSelectingReportingUnitFromEntityId(foundationId, {
          type: "FOUNDATION",
          id: foundationId,
        }),
      ),
      levelId: levelIdToUse,
      planarGraphId: foundationId,
      settings: foundationSettingsFromConfig,
      subSystem: foundationSubSystem,
      perimeter,
    };
  }

  return foundationSystem;
}

export default memo(select, generate, compare);
